import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Home, Calendar, Users, MessageCircle, User, LogOut, Shield, UserCog, Mail, Megaphone, Menu, X, BarChart3, MessageSquare, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import InstallBanner from './InstallBanner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ user, logout }) {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [newPosts, setNewPosts] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_f697ed33-4c13-453f-bfe2-564f171bf1d2/artifacts/4slporqq_1%20%281%29.png";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    // Check initial scroll position
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch pending applications count for admin/chapter leaders
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (user?.is_admin || user?.is_area_manager) {
        try {
          const response = await axios.get(`${API}/admin/pending-count`);
          setPendingCount(response.data.count || 0);
        } catch (error) {
          console.error('Failed to fetch pending count');
        }
      }
    };
    
    fetchPendingCount();
    // Refresh every 60 seconds
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/messages/unread-count`);
        setUnreadMessages(response.data.count || 0);
      } catch (error) {
        console.error('Failed to fetch unread count:', error.message);
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Clear post badge when visiting community page
  useEffect(() => {
    if (location.pathname === '/community') {
      setNewPosts(0);
    }
    if (location.pathname.startsWith('/messages')) {
      setUnreadMessages(0);
    }
    // Close more menu on navigation
    setMoreOpen(false);
  }, [location.pathname]);

  // Set PWA app badge for total notifications
  useEffect(() => {
    const updateAppBadge = async () => {
      try {
        const response = await axios.get(`${API}/notifications/count`);
        const data = response.data;
        const total = data.total || 0;
        setNewPosts(data.new_posts || 0);
        
        if ('setAppBadge' in navigator) {
          if (total > 0) {
            navigator.setAppBadge(total);
          } else {
            navigator.clearAppBadge();
          }
        }
      } catch (error) {
        console.error('Failed to update app badge:', error.message);
      }
    };
    
    updateAppBadge();
    const interval = setInterval(updateAppBadge, 30000);

    // Subscribe to Web Push Notifications
    const subscribeToPush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        
        const registration = await navigator.serviceWorker.ready;
        
        // Check if already subscribed
        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) {
          // Already subscribed, send token to SW for background use
          const token = localStorage.getItem('token');
          if (token && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'STORE_TOKEN', token });
          }
          return;
        }
        
        // Get VAPID public key
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        if (!vapidKey) return;
        
        // Convert base64url to Uint8Array
        const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
        const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        
        // Subscribe
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: outputArray
        });
        
        // Send subscription to backend
        await axios.post(`${API}/push/subscribe`, subscription.toJSON());
        
        // Send token to SW
        const token = localStorage.getItem('token');
        if (token && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'STORE_TOKEN', token });
        }
      } catch (error) {
        // Push not supported or permission denied — not critical
        console.log('Push subscription skipped:', error.message);
      }
    };
    subscribeToPush();

    return () => clearInterval(interval);
  }, [user]);

  const navItems = [
    { path: '/home', label: 'Home', icon: Home },
    { path: '/community', label: 'Feed', icon: MessageCircle },
    { path: '/marketplace', label: 'Marketplace', icon: Megaphone },
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/directory', label: 'Directory', icon: Users },
    { path: '/messages', label: 'Messages', icon: Mail },
  ];

  // Bottom tab bar items (mobile) — 5 core items
  const bottomTabs = [
    { path: '/home', label: 'Home', icon: Home },
    { path: '/community', label: 'Feed', icon: MessageCircle },
    { path: '/marketplace', label: 'Market', icon: Megaphone },
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/directory', label: 'Directory', icon: Users },
  ];

  // Get display name - use display_name if set, otherwise first name from full_name
  const displayName = user?.display_name || user?.full_name?.split(' ')[0];

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      {/* Desktop Navigation - only shows on large screens (1024px+) */}
      <nav className={`hidden lg:block fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm transition-all duration-300 ${scrolled ? 'py-2' : 'py-4'}`} data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <Link to="/community" className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Mom's Biz Network" 
                className={`transition-all duration-300 ${scrolled ? 'h-14 w-14' : 'h-20 w-20'}`} 
              />
              <span className={`font-bold text-[#5b7f95] transition-all duration-300 ${scrolled ? 'text-base' : 'text-xl'}`}>Mom's Biz Network</span>
            </Link>

            <div className="flex items-center gap-1">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link key={path} to={path}>
                  <div className="relative group">
                    <Button
                      variant={location.pathname === path ? 'default' : 'ghost'}
                      size="icon"
                      className={`rounded-full transition-all w-12 h-12 ${location.pathname === path ? 'bg-[#5b7f95] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      data-testid={`nav-${label.toLowerCase()}-btn`}
                    >
                      <Icon className="w-6 h-6" />
                    </Button>
                    {label === 'Messages' && unreadMessages > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid="unread-messages-badge">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    )}
                    {label === 'Feed' && newPosts > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#cb9944] text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid="new-posts-badge">
                        {newPosts > 99 ? '99+' : newPosts}
                      </span>
                    )}
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                      {label}
                    </span>
                  </div>
                </Link>
              ))}

              {user?.is_admin && (
                <Link to="/admin">
                  <div className="relative group">
                    <Button
                      variant={location.pathname === '/admin' ? 'default' : 'ghost'}
                      size="icon"
                      className={`rounded-full w-12 h-12 ${location.pathname === '/admin' ? 'bg-[#5b7f95] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      data-testid="nav-admin-btn"
                    >
                      <Shield className="w-6 h-6" />
                    </Button>
                    {pendingCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                        {pendingCount > 99 ? '99+' : pendingCount}
                      </span>
                    )}
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                      Admin
                    </span>
                  </div>
                </Link>
              )}

              {user?.is_area_manager && !user?.is_admin && (
                <Link to="/area-manager">
                  <div className="relative group">
                    <Button
                      variant={location.pathname === '/area-manager' ? 'default' : 'ghost'}
                      size="icon"
                      className={`rounded-full w-12 h-12 ${location.pathname === '/area-manager' ? 'bg-[#cb9944] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      data-testid="nav-chapter-leader-btn"
                    >
                      <UserCog className="w-6 h-6" />
                    </Button>
                    {pendingCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                        {pendingCount > 99 ? '99+' : pendingCount}
                      </span>
                    )}
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                      My Chapter
                    </span>
                  </div>
                </Link>
              )}

              <Link to="/profile">
                <div className="relative group">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full w-12 h-12 text-gray-600 hover:bg-gray-100"
                    data-testid="nav-profile-btn"
                  >
                    <User className="w-6 h-6" />
                  </Button>
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    {displayName}
                  </span>
                </div>
              </Link>

              <div className="relative group">
                <Button
                  variant="ghost"
                  onClick={logout}
                  size="icon"
                  className="rounded-full w-12 h-12 text-gray-600 hover:bg-gray-100"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-6 h-6" />
                </Button>
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                  Logout
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile & Tablet Navigation - Top logo bar + Bottom tab bar (shows below 1024px) */}
      {/* Top bar: just logo */}
      <nav className="lg:hidden fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" data-testid="mobile-nav">
        <div className={`transition-all duration-300 overflow-hidden ${scrolled ? 'h-0 opacity-0' : 'h-auto opacity-100 py-3'}`}>
          <Link to="/community" className="flex items-center justify-center">
            <img src={LOGO_URL} alt="Mom's Biz Network" className="h-24 w-24" />
          </Link>
        </div>
      </nav>

      {/* Bottom Tab Bar (mobile only) */}
      <div className="lg:hidden fixed bottom-0 w-full z-50 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]" data-testid="bottom-tab-bar">
        <div className="flex items-center justify-around py-1 px-1 max-w-lg mx-auto">
          {bottomTabs.map(({ path, label, icon: Icon }) => {
            const isActive = location.pathname === path;
            return (
              <Link key={path} to={path} className="relative flex flex-col items-center min-w-0 flex-1">
                <div className={`flex flex-col items-center py-1.5 px-1 rounded-xl transition-colors ${isActive ? 'text-[#5b7f95]' : 'text-gray-400'}`}>
                  <Icon className="w-5 h-5" />
                  <span className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-[#5b7f95]' : 'text-gray-400'}`}>{label}</span>
                </div>
                {label === 'Feed' && newPosts > 0 && (
                  <span className="absolute top-0 right-1/4 bg-[#cb9944] text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                    {newPosts > 99 ? '99+' : newPosts}
                  </span>
                )}
              </Link>
            );
          })}
          {/* More button */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className="relative flex flex-col items-center min-w-0 flex-1"
            data-testid="more-menu-btn"
          >
            <div className={`flex flex-col items-center py-1.5 px-1 rounded-xl transition-colors ${moreOpen ? 'text-[#5b7f95]' : 'text-gray-400'}`}>
              <Menu className="w-5 h-5" />
              <span className={`text-[10px] mt-0.5 font-medium ${moreOpen ? 'text-[#5b7f95]' : 'text-gray-400'}`}>More</span>
            </div>
            {(unreadMessages > 0 || pendingCount > 0) && (
              <span className="absolute top-0 right-1/4 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {(unreadMessages + pendingCount) > 99 ? '99+' : unreadMessages + pendingCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* More Menu Overlay (mobile) */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="more-menu-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
          <div className="absolute bottom-16 left-4 right-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
            <div className="p-4 space-y-1">
              <Link to="/messages" onClick={() => setMoreOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-10 h-10 rounded-full bg-[#5b7f95]/10 flex items-center justify-center relative">
                  <Mail className="w-5 h-5 text-[#5b7f95]" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-[#2d3748]">Messages</p>
                  <p className="text-xs text-[#64748b]">Direct messages</p>
                </div>
              </Link>

              <Link to="/group-chats" onClick={() => setMoreOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-10 h-10 rounded-full bg-[#5b7f95]/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#5b7f95]" />
                </div>
                <div>
                  <p className="font-medium text-[#2d3748]">Group Chats</p>
                  <p className="text-xs text-[#64748b]">Chapter & leader chats</p>
                </div>
              </Link>

              <Link to="/profile" onClick={() => setMoreOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-10 h-10 rounded-full bg-[#5b7f95]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#5b7f95]" />
                </div>
                <div>
                  <p className="font-medium text-[#2d3748]">{displayName}</p>
                  <p className="text-xs text-[#64748b]">View & edit your profile</p>
                </div>
              </Link>

              {user?.is_admin && (
                <Link to="/admin" onClick={() => setMoreOpen(false)}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#5b7f95]/10 flex items-center justify-center relative">
                    <Shield className="w-5 h-5 text-[#5b7f95]" />
                    {pendingCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                        {pendingCount}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#2d3748]">Admin Dashboard</p>
                    <p className="text-xs text-[#64748b]">Manage members & settings</p>
                  </div>
                </Link>
              )}

              {user?.is_area_manager && !user?.is_admin && (
                <Link to="/area-manager" onClick={() => setMoreOpen(false)}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#cb9944]/10 flex items-center justify-center relative">
                    <UserCog className="w-5 h-5 text-[#cb9944]" />
                    {pendingCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                        {pendingCount}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#2d3748]">My Chapter</p>
                    <p className="text-xs text-[#64748b]">Chapter leader dashboard</p>
                  </div>
                </Link>
              )}

              {(user?.is_admin || user?.is_area_manager) && (
                <Link to="/reports" onClick={() => setMoreOpen(false)}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#5b7f95]/10 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-[#5b7f95]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2d3748]">Reports</p>
                    <p className="text-xs text-[#64748b]">Engagement & analytics</p>
                  </div>
                </Link>
              )}

              <div className="border-t border-gray-100 pt-1 mt-1">
                <button onClick={() => { setMoreOpen(false); logout(); }}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-red-50 transition-colors w-full">
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                    <LogOut className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-red-600">Logout</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - adjust padding for mobile (top logo + bottom tabs) and desktop (top nav) */}
      <main className={`flex-1 transition-all duration-300 pb-20 lg:pb-12 ${scrolled ? 'pt-4 lg:pt-20' : 'pt-32 lg:pt-28'}`}>
        <Outlet />
      </main>

      {/* Footer with Social Links */}
      <footer className="bg-[#2d3748] text-white py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Mom's Biz Network" 
                className="h-12 w-12 rounded-full bg-white p-1" 
              />
              <span className="font-semibold text-lg">Mom's Biz Network</span>
            </div>
            
            <div className="flex items-center gap-6">
              <a 
                href="https://www.instagram.com/momsbiznetwork/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[#cb9944] transition-colors"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="text-sm">Instagram</span>
              </a>
              <a 
                href="https://www.facebook.com/groups/momsbiznetwork" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[#cb9944] transition-colors"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm">Facebook</span>
              </a>
            </div>
            
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Mom's Biz Network. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <InstallBanner />
    </div>
  );
}
